import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { LayoutService } from '../../core/services/layout.service';
import { I18nService } from '../../core/services/i18n.service';
import { IconPipe } from '../../shared/pipes/icon.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { GameService, ApiGame } from '../../core/services/game.service';
import { ChallengeService, ApiChallenge } from '../../core/services/challenge.service';
import { PlayersService, ApiPlayerListItem } from '../../core/services/players.service';
import { ModerationService } from '../../core/services/moderation.service';
import { RewardsService } from '../../core/services/rewards.service';
import { CategoryService } from '../../core/services/category.service';
import { environment } from '../../../environments/environment';

interface ChallengeStatusCounts {
  upcoming: number;
  ongoing: number;
  ended: number;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [RouterLink, IconPipe, TranslatePipe],
  templateUrl: './dashboard.component.html',
})
export class DashboardComponent implements OnInit {
  private layoutService = inject(LayoutService);
  protected i18n = inject(I18nService);
  private gameService = inject(GameService);
  private challengeService = inject(ChallengeService);
  private playersService = inject(PlayersService);
  private moderationService = inject(ModerationService);
  private rewardsService = inject(RewardsService);
  private categoryService = inject(CategoryService);

  isLoading = signal(true);

  totalPlayers    = signal(0);
  activePlayers   = signal(0);
  totalGames      = signal(0);
  activeGames     = signal(0);
  totalRewards    = signal(0);
  totalCategories = signal(0);

  challengeCounts  = signal<ChallengeStatusCounts>({ upcoming: 0, ongoing: 0, ended: 0 });
  moderationCounts = signal({ pending: 0, actioned: 0, dismissed: 0, all: 0 });
  topGames = signal<ApiGame[]>([]);
  topChallenges = signal<ApiChallenge[]>([]);

  deviceCounts = signal({ ios: 0, android: 0, web: 0 });
  countryCounts = signal<{ name: string; code?: string; count: number }[]>([]);

  hoveredChallengeSeg = signal<string | null>(null);

  readonly totalChallenges = computed(() =>
    this.challengeCounts().upcoming + this.challengeCounts().ongoing + this.challengeCounts().ended
  );

  readonly suspendedPlayers = computed(() => Math.max(0, this.totalPlayers() - this.activePlayers()));

  readonly playerActivePct = computed(() => {
    const total = this.totalPlayers();
    return total ? Math.round((this.activePlayers() / total) * 100) : 0;
  });

  // SVG donut ring math — circumference-based stroke-dasharray trick, no chart lib needed.
  readonly ringCircumference = 2 * Math.PI * 54;

  // Semicircular gauge for Player breakdown — half the ring circumference (180°),
  // arc drawn along a half-circle path instead of a full donut.
  readonly gaugeCircumference = Math.PI * 80;

  readonly gaugeActiveOffset = computed(() =>
    this.gaugeCircumference * (1 - this.playerActivePct() / 100)
  );

  // Fixed-size grid of squares visualizing the active/suspended split proportionally,
  // regardless of how large the real player counts are.
  private readonly playerGridSize = 18;

  readonly playerGridSquares = computed(() => {
    const total = this.totalPlayers();
    if (!total) return [];
    const activeSquares = Math.round((this.activePlayers() / total) * this.playerGridSize);
    return Array.from({ length: this.playerGridSize }, (_, i) => i < activeSquares);
  });

  readonly maxGamePlays = computed(() =>
    Math.max(...this.topGames().map(g => g.totalPlays || 0), 1)
  );

  readonly maxChallengeParticipants = computed(() =>
    Math.max(...this.topChallenges().map(c => c.participants || 0), 1)
  );

  readonly moderationTotal = computed(() => {
    const m = this.moderationCounts();
    return Math.max(m.pending + m.actioned + m.dismissed, 1);
  });

  readonly modResolvedCircumference = 2 * Math.PI * 27;

  readonly modResolvedPct = computed(() => {
    const m = this.moderationCounts();
    const total = m.pending + m.actioned + m.dismissed;
    return total ? Math.round(((m.actioned + m.dismissed) / total) * 100) : 0;
  });

  readonly modResolvedOffset = computed(() =>
    this.modResolvedCircumference * (1 - this.modResolvedPct() / 100)
  );

  readonly deviceSampleTotal = computed(() => {
    const d = this.deviceCounts();
    return d.ios + d.android + d.web;
  });

  readonly deviceTotal = computed(() => Math.max(this.deviceSampleTotal(), 1));

  // Multi-segment donut — circumference-based stroke-dasharray trick; each segment
  // gets its own dash length + rotation offset so the arcs sit end-to-end.
  private buildDonutSegments(
    segments: { key: string; label: string; value: number; color: string; icon?: string }[],
    total: number
  ) {
    let acc = 0;
    return segments.map(seg => {
      const frac = total ? seg.value / total : 0;
      const dash = frac * this.ringCircumference;
      const offset = -acc * this.ringCircumference;
      acc += frac;
      return { ...seg, dash, offset, pct: Math.round(frac * 100) };
    });
  }

  readonly deviceDonutSegments = computed(() =>
    this.buildDonutSegments(
      [
        { key: 'ios',     label: 'dash.ios',     value: this.deviceCounts().ios,     color: 'var(--accent)',  icon: 'phone' },
        { key: 'android', label: 'dash.android', value: this.deviceCounts().android, color: 'var(--success)', icon: 'phone' },
        { key: 'web',     label: 'dash.web',     value: this.deviceCounts().web,     color: 'var(--info)',    icon: 'globe' },
      ],
      this.deviceTotal()
    )
  );

  readonly challengeDonutSegments = computed(() =>
    this.buildDonutSegments(
      [
        { key: 'ongoing',  label: 'dash.ongoing',  value: this.challengeCounts().ongoing,  color: 'var(--accent)' },
        { key: 'upcoming', label: 'dash.upcoming', value: this.challengeCounts().upcoming, color: 'var(--success)' },
        { key: 'ended',    label: 'dash.ended',    value: this.challengeCounts().ended,    color: 'var(--danger)' },
      ],
      this.totalChallenges()
    )
  );

  readonly maxCountryCount = computed(() =>
    Math.max(...this.countryCounts().map(c => c.count), 1)
  );

  readonly countryTotal = computed(() =>
    Math.max(this.countryCounts().reduce((sum, c) => sum + c.count, 0), 1)
  );

  ngOnInit(): void {
    this.layoutService.setPage('page.dashboard', 'sub.dashboard');
    this.load();
  }

  load(): void {
    this.isLoading.set(true);

    forkJoin({
      games:         this.gameService.getGames(1, 100, {}),
      activeGames:   this.gameService.getGames(1, 1, { isActive: true }),
      challenges:    this.challengeService.list(1, 100, {}),
      challUpcoming: this.challengeService.list(1, 1, { status: 'upcoming' }),
      challOngoing:  this.challengeService.list(1, 1, { status: 'ongoing' }),
      challEnded:    this.challengeService.list(1, 1, { status: 'ended' }),
      players:       this.playersService.list(1, 1, {}),
      playersSample: this.playersService.list(1, 200, {}),
      activePlayers: this.playersService.list(1, 1, { status: 'active' }),
      moderation:    this.moderationService.list(1, 1, { status: 'all' }),
      rewards:       this.rewardsService.list(1, 1, {}),
      categories:    this.categoryService.getCategories(),
    }).subscribe({
      next: res => {
        const games = res.games.data?.list ?? [];
        this.totalGames.set(res.games.data?.total ?? games.length);
        this.activeGames.set(res.activeGames.data?.total ?? 0);
        this.topGames.set(
          [...games].sort((a, b) => (b.totalPlays || 0) - (a.totalPlays || 0)).slice(0, 5)
        );

        this.challengeCounts.set({
          upcoming: res.challUpcoming.data?.total ?? 0,
          ongoing:  res.challOngoing.data?.total ?? 0,
          ended:    res.challEnded.data?.total ?? 0,
        });

        const challenges = res.challenges.data?.list ?? [];
        this.topChallenges.set(
          [...challenges].sort((a, b) => (b.participants || 0) - (a.participants || 0)).slice(0, 5)
        );

        this.totalPlayers.set(res.players.data?.total ?? 0);
        this.activePlayers.set(res.activePlayers.data?.total ?? 0);

        const sample = res.playersSample.data?.list ?? [];
        this.deviceCounts.set(this.countDevices(sample));
        this.countryCounts.set(this.countCountries(sample));

        this.moderationCounts.set(res.moderation.data?.statusCounts ?? { pending: 0, actioned: 0, dismissed: 0, all: 0 });

        this.totalRewards.set(res.rewards.data?.total ?? 0);
        this.totalCategories.set(res.categories.data?.total ?? 0);

        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  private countDevices(players: ApiPlayerListItem[]): { ios: number; android: number; web: number } {
    const counts = { ios: 0, android: 0, web: 0 };
    for (const p of players) {
      if (p.deviceType === 'ios' || p.deviceType === 'android' || p.deviceType === 'web') {
        counts[p.deviceType]++;
      }
    }
    return counts;
  }

  private countCountries(players: ApiPlayerListItem[]): { name: string; code?: string; count: number }[] {
    const map = new Map<string, { code?: string; count: number }>();
    for (const p of players) {
      const name = p.countryId?.name;
      if (!name) continue;
      const entry = map.get(name) ?? { code: p.countryId?.countryCode, count: 0 };
      entry.count++;
      map.set(name, entry);
    }
    return [...map.entries()]
      .map(([name, { code, count }]) => ({ name, code, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
  }

  countryPct(count: number): number {
    return Math.round((count / this.countryTotal()) * 100);
  }

  countryFlag(code: string | undefined): string {
    if (!code || code.length !== 2) return '';
    const base = 0x1f1e6 - 'A'.charCodeAt(0);
    return [...code.toUpperCase()].map(ch => String.fromCodePoint(base + ch.charCodeAt(0))).join('');
  }

  challengeName(c: ApiChallenge): string {
    return this.i18n.lang() === 'ar' ? (c.title?.ar || c.title?.en) : (c.title?.en || c.title?.ar);
  }

  gameName(g: ApiGame): string {
    return this.i18n.lang() === 'ar' ? (g.name?.ar || g.name?.en) : (g.name?.en || g.name?.ar);
  }

  gameImageUrl(image: string): string {
    return `${environment.apiUrl}/${image.replace(/^\//, '')}`;
  }

  fmt(n: number): string {
    return n.toLocaleString(this.i18n.lang() === 'ar' ? 'ar-EG' : 'en-US');
  }

  initials(name: string): string {
    return (name || '?').trim().slice(0, 2).toUpperCase();
  }
}
